import type { LineItem } from '@fdc/__generated__/graphql';
import {
  GTM_EVENT_TYPES,
  PRODUCT_DETAIL_LIST_ID,
  PRODUCT_DETAIL_LIST_NAME,
} from '@fdc/constants/analytics-constants';
import {
  ProductAnalyticsOptions,
  RemoveFromCartEcommercePayload,
  ViewItemListEcommerceItem,
} from '@fdc/constants/analytics-types';
import { ProductSummaryUnion } from '@fdc/types';
import { pushGtmAnalyticsEvent } from '@fdc/utils/analytics-utils';
import {
  type CartFailedValidation,
  mapLineItemToRemoveFromCartItem,
} from '@fdc/utils/cart-remove-from-cart-analytics';
import {
  getItemAttribution,
  getStoredProductAnalyticsOptions,
  resolveCartRemoveListAttribution,
  resolveListAttributionContext,
} from '@fdc/utils/item-attribution-analytics';
import { mapProductSummaryToViewItemListItem } from '@fdc/utils/view-item-list-analytics';

const CURRENCY = 'NZD';

type RemoveFromCartItemParams = {
  product: Partial<ProductSummaryUnion>;
  quantity: number;
  itemListId: string;
  itemListName: string;
  index: number;
  options?: ProductAnalyticsOptions;
};

function shouldUseStoredAttributionOptions(
  itemListId: string,
  itemListName: string,
  productSku: string
): boolean {
  const stored = getItemAttribution();
  return (
    itemListId === PRODUCT_DETAIL_LIST_ID &&
    itemListName === PRODUCT_DETAIL_LIST_NAME &&
    stored !== null &&
    stored.item_id === productSku &&
    Boolean(
      stored.item_list_id && stored.item_list_name && stored.index != null
    )
  );
}

type MergeRemoveFromCartAnalyticsOptionsParams = {
  itemListId: string;
  itemListName: string;
  productSku: string;
  options?: ProductAnalyticsOptions;
};

function mergeRemoveFromCartAnalyticsOptions({
  itemListId,
  itemListName,
  productSku,
  options,
}: MergeRemoveFromCartAnalyticsOptionsParams): ProductAnalyticsOptions {
  const storedOptions = shouldUseStoredAttributionOptions(
    itemListId,
    itemListName,
    productSku
  )
    ? getStoredProductAnalyticsOptions(productSku)
    : undefined;

  return {
    ...options,
    sortBy: options?.sortBy ?? storedOptions?.sortBy,
    promotion: options?.promotion ?? storedOptions?.promotion,
    adId: options?.adId ?? storedOptions?.adId,
    cartSelectedUom: options?.cartSelectedUom,
    cartQuantity: options?.cartQuantity,
  };
}

function buildItemWithAttribution({
  product,
  quantity,
  itemListId,
  itemListName,
  index,
  options,
}: RemoveFromCartItemParams): ViewItemListEcommerceItem {
  return {
    ...mapProductSummaryToViewItemListItem(
      product,
      index,
      itemListId,
      itemListName,
      options
    ),
    quantity,
  };
}

type BuildRemoveFromCartPayloadParams = {
  product: Partial<ProductSummaryUnion>;
  quantityRemoved: number;
  itemListId: string;
  itemListName: string;
  index: number;
  options?: ProductAnalyticsOptions;
};

/**
 * Builds the remove_from_cart ecommerce payload for one product removed from cart.
 * value = price * quantity for the volume being removed.
 */
export function buildRemoveFromCartPayload({
  product,
  quantityRemoved,
  itemListId,
  itemListName,
  index,
  options,
}: BuildRemoveFromCartPayloadParams): RemoveFromCartEcommercePayload {
  const listContext = resolveListAttributionContext(
    itemListId,
    itemListName,
    index,
    product.sku ?? ''
  );
  const analyticsOptions = mergeRemoveFromCartAnalyticsOptions({
    itemListId,
    itemListName,
    productSku: product.sku ?? '',
    options,
  });
  const item = buildItemWithAttribution({
    product,
    quantity: quantityRemoved,
    itemListId: listContext.itemListId,
    itemListName: listContext.itemListName,
    index: listContext.itemListIndex,
    options: analyticsOptions,
  });
  const value = Math.max(0, item.price * quantityRemoved);
  return {
    currency: CURRENCY,
    value,
    algolia_query_id: analyticsOptions.queryId ?? null,
    items: [item],
  };
}

type BuildRemoveFromCartPayloadFromLineItemParams = {
  lineItem: LineItem;
  quantityRemoved: number;
  cartId: string;
  failedValidations?: readonly CartFailedValidation[] | null;
};

/**
 * Builds remove_from_cart payload from a cart line item (cart page context).
 */
export function buildRemoveFromCartPayloadFromLineItem({
  lineItem,
  quantityRemoved,
  cartId,
  failedValidations,
}: BuildRemoveFromCartPayloadFromLineItemParams): RemoveFromCartEcommercePayload {
  const { itemListId, itemListName } = resolveCartRemoveListAttribution(cartId);
  const item = mapLineItemToRemoveFromCartItem({
    item: lineItem,
    quantity: quantityRemoved,
    itemListId,
    itemListName,
    failedValidations,
  });
  const value = Math.max(0, item.price * quantityRemoved);
  return {
    currency: CURRENCY,
    value,
    algolia_query_id: null,
    items: [item],
  };
}

type PushRemoveFromCartAnalyticsAfterSuccessParams = {
  product: Partial<ProductSummaryUnion>;
  previousQuantity: number;
  newValue: number;
  itemListId: string;
  itemListName: string;
  itemListIndex: number;
  options?: ProductAnalyticsOptions;
};

/**
 * Pushes remove_from_cart when the cart line quantity decreased.
 * Fires on stepper minus or remove after optimistic cart sanitization.
 */
export function pushRemoveFromCartAnalyticsAfterSuccess({
  product,
  previousQuantity,
  newValue,
  itemListId,
  itemListName,
  itemListIndex,
  options,
}: PushRemoveFromCartAnalyticsAfterSuccessParams): void {
  const quantityRemoved = previousQuantity - newValue;
  if (quantityRemoved <= 0) {
    return;
  }

  pushRemoveFromCartEvent(
    buildRemoveFromCartPayload({
      product,
      quantityRemoved,
      itemListId,
      itemListName,
      index: itemListIndex,
      options,
    })
  );
}

type PushRemoveFromCartAnalyticsForLineItemParams = {
  lineItem: LineItem;
  previousQuantity: number;
  newQuantity: number;
  cartId: string;
  failedValidations?: readonly CartFailedValidation[] | null;
};

export function pushRemoveFromCartAnalyticsForLineItem({
  lineItem,
  previousQuantity,
  newQuantity,
  cartId,
  failedValidations,
}: PushRemoveFromCartAnalyticsForLineItemParams): void {
  const quantityRemoved = previousQuantity - newQuantity;
  if (quantityRemoved <= 0) {
    return;
  }

  pushRemoveFromCartEvent(
    buildRemoveFromCartPayloadFromLineItem({
      lineItem,
      quantityRemoved,
      cartId,
      failedValidations,
    })
  );
}

/**
 * Pushes remove_from_cart to the data layer.
 */
export function pushRemoveFromCartEvent(
  payload: RemoveFromCartEcommercePayload
): void {
  if (typeof window === 'undefined') {
    return;
  }
  pushGtmAnalyticsEvent({
    event: GTM_EVENT_TYPES.REMOVE_FROM_CART,
    ecommerce: payload,
  });
}
